// Never fall back to `_id`: Mongoose mints a throwaway one on every read for
// blocks stored without it, so a note saved against it could never be found again.
export function getAnimationSectionId(section, sectionNumber, index) {
  if (section?.id) return String(section.id);
  return `${sectionNumber}:${index}`;
}

export function makeNoteKey(sectionNumber, animationSectionId) {
  return `${sectionNumber}::${animationSectionId}`;
}

function stripHtmlToText(html) {
  return String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
}

export function isVerbatimShareSection(section) {
  const category = String(section?.semanticCategory || "")
    .toLowerCase()
    .trim();
  if (category === "ayah" || category === "hadith") return true;
  const name = String(section?.name || "");
  return /ayah|hadith|quote/i.test(name);
}

export function getShareSectionKind(section) {
  const category = String(section?.semanticCategory || "")
    .toLowerCase()
    .trim();
  if (category === "ayah" || category === "hadith") return category;
  if (isVerbatimShareSection(section)) return "quote";
  return "lesson";
}

export function extractSectionPlainText(section) {
  const texts = [];
  const walk = (item) => {
    if (!item) return;
    const className = item.className || "";
    if (
      className.includes("image") ||
      className === "LottieAnimation" ||
      className === "YouTubeVideo"
    ) {
      (item.subSections || []).forEach(walk);
      (item.miniSections || []).forEach(walk);
      return;
    }
    if (item.content) {
      const text = stripHtmlToText(item.content);
      if (text) texts.push(text);
    }
    (item.subSections || []).forEach(walk);
    (item.miniSections || []).forEach(walk);
  };
  walk(section);
  return texts.join(" ").replace(/\s+/g, " ").trim();
}

export function extractSectionShareText(section) {
  const texts = [];
  const walk = (item) => {
    if (!item) return;
    const className = item.className || "";
    if (
      className.includes("image") ||
      className === "LottieAnimation" ||
      className === "YouTubeVideo"
    ) {
      (item.subSections || []).forEach(walk);
      (item.miniSections || []).forEach(walk);
      return;
    }
    if (item.content) {
      const text = stripHtmlToText(item.content);
      if (text) texts.push(text);
    }
    (item.subSections || []).forEach(walk);
    (item.miniSections || []).forEach(walk);
  };
  walk(section);
  return texts.join("\n\n").trim();
}

export function imageUrlFromNode(item) {
  if (!item) return "";
  const className = String(item.className || "").toLowerCase();
  if (!className.includes("image")) return "";
  if (item.imageFilePath) return String(item.imageFilePath).trim();
  if (Array.isArray(item.imageOptions) && item.imageOptions.length > 0) {
    const selectedIndex =
      typeof item.selectedImageIndex === "number" ? item.selectedImageIndex : 0;
    return String(
      item.imageOptions[selectedIndex] || item.imageOptions[0] || ""
    ).trim();
  }
  return "";
}

function walkForImage(item) {
  const direct = imageUrlFromNode(item);
  if (direct) return direct;
  for (const sub of item?.subSections || []) {
    const found = walkForImage(sub);
    if (found) return found;
  }
  for (const mini of item?.miniSections || []) {
    const found = walkForImage(mini);
    if (found) return found;
  }
  return "";
}

export function findFirstLessonImageUrl(lesson) {
  const sections = Array.isArray(lesson?.lessonAnimation)
    ? lesson.lessonAnimation
    : [];
  for (const section of sections) {
    const found = walkForImage(section);
    if (found) return found;
  }
  return String(lesson?.sectionImage || "").trim();
}

export function extractLessonPlainText(lessonAnimation, maxChars = 2500) {
  const sections = Array.isArray(lessonAnimation) ? lessonAnimation : [];
  const combined = sections
    .map((section, index) => {
      const text = extractSectionPlainText(section);
      return text ? `${index + 1}. ${text}` : "";
    })
    .filter(Boolean)
    .join("\n");
  if (combined.length <= maxChars) return combined;
  return `${combined.slice(0, maxChars).trim()}…`;
}

export function sectionHasTextContent(section) {
  const hasText = (item) => {
    if (!item) return false;
    const className = item.className || "";
    if (
      className.includes("image") ||
      className === "LottieAnimation" ||
      className === "YouTubeVideo"
    ) {
      return false;
    }
    if (item.content) {
      const text = String(item.content)
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      if (text) return true;
    }
    return (
      (item.subSections || []).some(hasText) ||
      (item.miniSections || []).some(hasText)
    );
  };
  return hasText(section);
}

export function findNote(notes, sectionNumber, animationSectionId) {
  return (notes || []).find(
    (note) =>
      note.sectionNumber === sectionNumber &&
      note.animationSectionId === animationSectionId
  );
}

export function compareDottedNumbers(a, b) {
  const partsA = String(a || "")
    .split(/[._]/)
    .map((part) => parseInt(part, 10) || 0);
  const partsB = String(b || "")
    .split(/[._]/)
    .map((part) => parseInt(part, 10) || 0);
  const length = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < length; i += 1) {
    const diff = (partsA[i] || 0) - (partsB[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

export function sortNotesByLessonOrder(notes) {
  return [...(notes || [])].sort((a, b) => {
    const lessonCmp = compareDottedNumbers(a.sectionNumber, b.sectionNumber);
    if (lessonCmp !== 0) return lessonCmp;
    const indexA =
      typeof a.animationSectionIndex === "number" && a.animationSectionIndex >= 0
        ? a.animationSectionIndex
        : Number.MAX_SAFE_INTEGER;
    const indexB =
      typeof b.animationSectionIndex === "number" && b.animationSectionIndex >= 0
        ? b.animationSectionIndex
        : Number.MAX_SAFE_INTEGER;
    return indexA - indexB;
  });
}

export function scrollToAnimationSection(animationSectionId) {
  if (!animationSectionId) return;
  const element = document.getElementById(`section-${animationSectionId}`);
  if (!element) return;
  const offset = element.getBoundingClientRect().top + window.scrollY - window.innerHeight * 0.3;
  window.scrollTo({
    top: Math.max(0, offset),
    behavior: "smooth",
  });
}
